/**
 * Helper for tRPC mutation wrappers that need to surface the
 * `CHANGE_REQUEST_REQUIRED` cause to the user via the confirm modal and
 * replay the mutation with `_confirmChangeRequest: true`.
 *
 * Usage from a `useUpdate*TRPC` hook:
 *
 *   const { requestConfirmation } = useChangeRequestConfirm();
 *
 *   const updateRisk = (input) =>
 *     withChangeRequestConfirm({
 *       input,
 *       run: (variables) => mutation.mutateAsync(variables),
 *       requestConfirmation,
 *     });
 *
 * The helper:
 *   1. invokes `run(input)`;
 *   2. on `CHANGE_REQUEST_REQUIRED` error, calls `requestConfirmation()`;
 *   3. if the user confirms, replays `run({ ...input, _confirmChangeRequest, _requesterComment })`;
 *   4. if the user cancels, re-throws the original error so the form
 *      layer can render it (currently `mapFormError` returns
 *      `{kind: 'change-request-required'}` for cancellation — FormContext
 *      treats that as a no-op submit).
 *
 * Spec reference: RSP-1528 Phase 6b.
 */
import { tryExtractChangeRequestRequired } from 'src/components/form/form/formError';
import type { ChangeRequestConfirmContextValue } from 'src/providers/change-request-confirm-context';

export const withChangeRequestConfirm = async <TInput, TOutput>({
  input,
  run,
  requestConfirmation,
}: {
  input: TInput;
  run: (variables: TInput) => Promise<TOutput>;
  requestConfirmation: ChangeRequestConfirmContextValue['requestConfirmation'];
}): Promise<TOutput> => {
  try {
    return await run(input);
  } catch (error) {
    if (!tryExtractChangeRequestRequired(error)) {
      throw error;
    }

    const confirmed = await requestConfirmation();
    if (!confirmed) {
      throw error;
    }

    // Spread the confirm flag fields onto the input. The wrapper input
    // types track the legacy GraphQL mutation shape (which doesn't
    // include `_confirmChangeRequest` / `_requesterComment`) but the
    // underlying tRPC procedure's Zod schema accepts both — runtime is
    // fine, TS needs the unknown round-trip.
    const retryInput = {
      ...input,
      _confirmChangeRequest: true,
      _requesterComment: confirmed.requesterComment || null,
    } as unknown as TInput;

    return await run(retryInput);
  }
};
